import { createApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

import {
  PricingScheme,
  PricingSchemeEditFormValues,
  PricingSchemeImpact,
} from "./schemeTypes";

const BASE = "/v1/admin/pricing-schemes";

const transformScheme = (res: any): PricingScheme => ({
  id: res.id,
  name: res.name,
  description: res.description ?? null,
  isActive: res.isActive,
  tenantCount: res.tenantCount ?? 0,
  items: (res.items ?? []).map((item: any) => ({
    id: item.id,
    keyCode: item.keyCode,
    adjustmentType: item.adjustmentType,
    value: item.value,
  })),
});

const transformImpact = (res: any): PricingSchemeImpact => ({
  tenantCount: res.tenantCount ?? 0,
  monthlyEffect: res.monthlyEffect ?? 0,
  unmeasured: res.unmeasured ?? [],
  tenants: (res.tenants ?? []).map((t: any) => ({
    tenantId: t.tenantId,
    tenantName: t.tenantName,
    vehicleCount: t.vehicleCount,
    monthlyEffect: t.monthlyEffect,
  })),
});

const transformOnSave = (formValues: PricingSchemeEditFormValues): any => ({
  id: formValues.id,
  name: formValues.name,
  description: formValues.description,
  isActive: formValues.isActive,
  items: formValues.items
    .filter((item) => item.keyCode)
    .map((item) => ({
      keyCode: item.keyCode,
      // Backend očekuje broj: 0 = Percent, 1 = Absolute.
      adjustmentType: item.adjustmentType === "Absolute" ? 1 : 0,
      value: Number(item.value ?? 0),
    })),
});

/*---------------------------------------------------------------------------------------
    SHEME — POPIS
-----------------------------------------------------------------------------------------*/
export const getPricingSchemesAPI = async (): Promise<PricingScheme[]> => {
  const response = await createApiCall(
    { path: BASE, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return (response.data?.results ?? []).map(transformScheme);
};

/*---------------------------------------------------------------------------------------
    SHEME — SPREMANJE
-----------------------------------------------------------------------------------------*/
export const savePricingSchemeAPI = async (
  formValues: PricingSchemeEditFormValues
): Promise<PricingScheme> => {
  const response = await createApiCall(
    { path: BASE, method: "POST", body: transformOnSave(formValues) },
    {
      getMessage: {
        success: () => "Shema je spremljena",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformScheme(response.data);
};

/*---------------------------------------------------------------------------------------
    SHEME — BRISANJE
-----------------------------------------------------------------------------------------*/
export const deletePricingSchemeAPI = async (schemeId: string): Promise<void> => {
  await createApiCall(
    { path: `${BASE}/${schemeId}`, method: "DELETE" },
    {
      getMessage: {
        success: () => "Shema je obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};

/*---------------------------------------------------------------------------------------
    SHEME — UČINAK

    Prikazuje se prije primjene, da se ne odobrava naslijepo.
-----------------------------------------------------------------------------------------*/
export const getPricingSchemeImpactAPI = async (
  schemeId: string
): Promise<PricingSchemeImpact> => {
  const response = await createApiCall(
    { path: `${BASE}/${schemeId}/impact`, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return transformImpact(response.data);
};
